import { createFeatureSelector, createSelector } from '@ngrx/store';
import {
  FILE_FEATURE_KEY,
  State,
  FilePartialState,
  fileAdapter
} from './file.reducer';
import { FileEntity } from './file.models';

const { selectAll, selectEntities } = fileAdapter.getSelectors();

/** Cutoff: files older than this are "archived" (1 year ago from now). */
function getArchiveCutoffMs(): number {
  const d = new Date();
  d.setFullYear(d.getFullYear() - 1);
  return d.getTime();
}

/**
 * Resolve a single date for a file: updatedAt, else createdAt, else parsed from filename (segment after first _).
 * Returns time in ms or null if unknown.
 */
export function getFileDateMs(file: FileEntity): number | null {
  if (file.updatedAt) {
    const t = new Date(file.updatedAt).getTime();
    if (!isNaN(t)) return t;
  }
  if (file.createdAt) {
    const t = new Date(file.createdAt).getTime();
    if (!isNaN(t)) return t;
  }
  if (file.name) {
    const part = file.name.split('_')[1];
    const dateStr = part ? part.split('.')[0] : '';
    if (dateStr) {
      const t = Date.parse(dateStr);
      if (!isNaN(t)) return t;
    }
  }
  return null;
}

/**
 * Sensitive verification files should never appear in the 1-year archived section.
 * Match by synthetic "ids" tag when available, with name fallback for safety.
 */
export function isArchiveExcludedFile(file: FileEntity): boolean {
  const tags = (file.tags || []).map(t => t.toLowerCase());
  if (tags.includes('ids')) return true;

  const name = (file.name || '').toLowerCase();
  const folder = (file.folder || '').toLowerCase();
  const fileKey = name.split('_')[0];

  // Stable keys used by customer-files title mapping.
  if (
    ['selfie', 'govid', 'persona', 'identity', 'id', 'ids', 'drug', 'card'].includes(fileKey) ||
    fileKey.startsWith('drugcard')
  ) {
    return true;
  }

  const fallbackSensitivePattern =
    /(?:^|[^A-Za-z0-9])(id|ids|govid|govids|passport|driver[_ -]?license|identity|persona|selfie|drug[_ -]?card(?:\d+)?|drug[ _-]?plan[ _-]?card|drug|card)(?:$|[^A-Za-z0-9])/i;

  return (
    fallbackSensitivePattern.test(name) ||
    fallbackSensitivePattern.test(folder)
  );
}

// Lookup the 'File' feature state managed by NgRx
export const getFileState = createFeatureSelector<FilePartialState, State>(
  FILE_FEATURE_KEY
);

export const getFilesLoading = createSelector(
  getFileState,
  (state: State) => state.loading
);

export const getFileError = createSelector(
  getFileState,
  (state: State) => state.error
);

export const getAllFile = createSelector(getFileState, (state: State) =>
  selectAll(state)
);

export const getFileEntities = createSelector(getFileState, (state: State) =>
  selectEntities(state)
);

export const getSelectedId = createSelector(
  getFileState,
  (state: State) => state.selectedId
);

export const getSelected = createSelector(
  getFileEntities,
  getSelectedId,
  (entities, selectedId) => selectedId && entities[selectedId]
);

/** Files with date within the last year (recent). */
export const getRecentFiles = createSelector(getAllFile, (files: FileEntity[]) => {
  const cutoffMs = getArchiveCutoffMs();
  return files.filter(f => {
    // Files excluded from archive should still be visible in recent list,
    // even when older than one year.
    if (isArchiveExcludedFile(f)) return true;
    const ms = getFileDateMs(f);
    return ms !== null && ms >= cutoffMs;
  });
});

/** Files older than one year (archived). */
export const getArchivedFiles = createSelector(getAllFile, (files: FileEntity[]) => {
  const cutoffMs = getArchiveCutoffMs();
  return files.filter(f => {
    if (isArchiveExcludedFile(f)) return false;
    const ms = getFileDateMs(f);
    return ms === null || ms < cutoffMs;
  });
});

/** All unique tag values across all files (for filter UI). */
export const getUniqueFileTags = createSelector(getAllFile, (files: FileEntity[]) => {
  const set = new Set<string>();
  files.forEach(f => (f.tags || []).forEach(t => set.add(t)));
  return Array.from(set).sort();
});

/** All unique folder values across all files (for folder filter UI). */
export const getUniqueFileFolders = createSelector(getAllFile, (files: FileEntity[]) => {
  const set = new Set<string>();
  files.forEach(f => {
    const folder = f.folder && f.folder.trim() !== '' ? f.folder : 'root';
    set.add(folder);
  });
  return Array.from(set).sort((a, b) => {
    if (a === 'root') return -1;
    if (b === 'root') return 1;
    return a.localeCompare(b);
  });
});
